import { Injectable, signal } from '@angular/core';

interface SelectedChat {
  id: string | null;
  title?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  /** Holds the list of all conversations */
  conversations = signal<any[]>([]);

  /** Stores the current prompt input */
  currentPrompt = signal('');

  /** Indicates whether to show the welcome message */
  showWelcome = signal(true);

  /** Stores the currently selected chat ID and optional title */
  selectedChat = signal<SelectedChat>({ id: null });

  /** Signal to indicate whether a new chat has been initiated */
  private newChatSignal = signal(false);

  /**
   * Resets the chat state to initial defaults
   * - Clears conversations
   * - Clears prompt
   * - Shows welcome screen
   * - Unselects current chat
   * - Triggers newChatSignal for 100ms
   *
   * @returns {boolean} True if reset is successful
   */
  resetChat(): boolean {
    this.conversations.set([]);
    this.currentPrompt.set('');
    this.showWelcome.set(true);
    this.selectedChat.set({ id: null });
    this.newChatSignal.set(true);
    setTimeout(() => this.newChatSignal.set(false), 100);
    return true;
  }

  /**
   * Indicates whether a new chat was triggered recently
   *
   * @returns {boolean} True if a new chat was initiated
   */
  newChat(): boolean {
    return this.newChatSignal();
  }

  /**
   * Sets the currently selected chat ID and optional title
   *
   * @param {string | null} chatId - ID of the selected chat
   * @param {string | null} [title] - Optional title of the chat
   */
  setSelectedChat(chatId: string | null, title?: string | null): void {
    this.selectedChat.set({ id: chatId, title });
  }

  /**
   * Retrieves the full selected chat object
   *
   * @returns {SelectedChat} The currently selected chat
   */
  getSelectedChat(): SelectedChat {
    return this.selectedChat();
  }

  /**
   * Retrieves only the ID of the selected chat
   *
   * @returns {string | null} The selected chat ID
   */
  getSelectedChatId(): string | null {
    return this.selectedChat().id;
  }

  /**
   * Retrieves the title of the selected chat (if any)
   *
   * @returns {string | null | undefined} The selected chat title
   */
  getSelectedChatTitle(): string | null | undefined {
    return this.selectedChat().title;
  }
}
